import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash/get';
import _throttle from 'lodash/throttle';
import { bindActionCreators } from 'redux';
import memoize from 'memoize-one';
import ReactTooltip from 'react-tooltip';
import {
  sendAnalyticsEvent,
  updateSRLWidgetsHeight,
} from '../../../actions/reports';
import {
  toggleInsightDetailsModal,
  getDashboardsWidgets,
  getSRLSurveys,
  getSRLSurveyResults,
  containerWidthChange,
} from '../../../actions/insights';
import {
  WIDGET_TYPE_TOP_EXTERNAL_LO_BAR_CHART,
  WIDGET_TYPE_ASSIGNMENT_SCORE_HISTOGRAM_CHART,
  WIDGET_TYPE_LOGIN_PIE_CHART,
  WIDGET_TYPE_TIME_SCORE_CHART,
  WIDGET_TYPE_TIME_ATTEMPTS_CHART,
  WIDGET_TYPE_COMPLETED_ASSIGNMENT,
} from '../../constants';
import CustomMuiDropdown from '../../../../components/CustomMuiDropdown';
import WidgetWrapper from './components/WidgetWrapper';
import WidgetEmptyState from './components/WidgetEmptyState';
import Loader from '../Loader';
import EmptySRLDashboard from './components/EmptySRLDashboard/EmptySRLDashboard';

import { VIEW_QQ_INSIGHTS_REPORT, selectAssignment } from '../../../analyticsEvents';

import GridLayout from './components/GridLayout/GridLayout';
import style from './QuantQualInsights.module.css';
import { getConfigObject } from '../../../util/config';


const MOBILE_XS = 425;
const WIDGET_HEIGHT = 426;
const WIDGET_HEIGHT_TALLER = 650;

class QuantQualInsights extends Component {
  static propTypes = {
    loading: PropTypes.bool,
    surveys: PropTypes.object,
    onTabChange: PropTypes.func,
    widgetsV2: PropTypes.array,
    liftAssignments: PropTypes.func,
    getSRLDropdown: PropTypes.func,
    refreshReport: PropTypes.func,
    sendAnalyticsEvent: PropTypes.func,
    courseAbstract: PropTypes.object,
    toggleInsightDetailsModal: PropTypes.func.isRequired,
    getDashboardsWidgets: PropTypes.func.isRequired,
    getSRLSurveys: PropTypes.func.isRequired,
    getSRLSurveyResults: PropTypes.func.isRequired,
    widgetDetailModalVisible: PropTypes.number,
    assignments: PropTypes.array,
    containerWidthChange: PropTypes.func,
    millieBot: PropTypes.shape({
      isLoaded: PropTypes.bool,
      start: PropTypes.func,
      stop: PropTypes.func,
      remove: PropTypes.func,
    }),
  }

  constructor(props) {
    super(props);
    const currentSurveyList = get(props.surveys, 'srlSurveyList', []);
    this.state = {
      srlDropdownAnchor: false,
      srlSelectedOption: currentSurveyList.length > 0 ? {
        label: currentSurveyList[0].name,
        value: currentSurveyList[0].id,
      } : {},
      clientWidth: 0,
    };
  }

  updateClientWidth = _throttle(() => {
    const clientWidth = window.innerWidth;
    const newHeight = clientWidth <= MOBILE_XS ? WIDGET_HEIGHT_TALLER : WIDGET_HEIGHT;
    if (this.props.SRLWidgetsHeight !== newHeight) {
      this.props.updateSRLWidgetsHeight(newHeight);
    }
    this.setState({ clientWidth });
  }, 300);

  componentDidMount() {
    const { courseAbstract } = this.props;
    const courseId = courseAbstract.id;
    this.props.getDashboardsWidgets(courseId);
    this.props.getSRLSurveys(courseId);
    this.props.sendAnalyticsEvent(VIEW_QQ_INSIGHTS_REPORT);
    this.updateClientWidth();
    window.addEventListener('resize', this.updateClientWidth);
  }

  componentDidUpdate(prevProps) {
    const prevSurveyList = get(prevProps.surveys, 'srlSurveyList', []);
    const currentSurveyList = get(this.props.surveys, 'srlSurveyList', []);

    if (this.props.liftAssignments) {
      this.props.liftAssignments(this.props.assignments);
    }

    if (currentSurveyList.length > 0 && prevSurveyList.length !== currentSurveyList.length) {
      const survey = currentSurveyList[0];
      this.setState({
        srlSelectedOption: {
          label: survey.name,
          value: survey.id,
        },
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateClientWidth);
  }

  getWidgetComponents = memoize(dWidgets => dWidgets
    .filter(dw => {
        const {
          FEATURE_USE_EXTERNAL_INSIGHTS,
          FEATURE_USE_SCORE_HISTOGRAM_CHART,
          FEATURE_USE_LOGIN_INSIGHTS,
          FEATURE_USE_TIME_SCORE_CHART,
          FEATURE_USE_TIME_ATTEMPTS_CHART,
          FEATURE_USE_COMPLETED_ASSIGNMENT,
        } = getConfigObject();

        return (
          (FEATURE_USE_EXTERNAL_INSIGHTS || dw.widget_type !== WIDGET_TYPE_TOP_EXTERNAL_LO_BAR_CHART) &&
          (FEATURE_USE_SCORE_HISTOGRAM_CHART || dw.widgetType !== WIDGET_TYPE_ASSIGNMENT_SCORE_HISTOGRAM_CHART) &&
          (FEATURE_USE_TIME_SCORE_CHART || dw.widgetType !== WIDGET_TYPE_TIME_SCORE_CHART) &&
          (FEATURE_USE_TIME_ATTEMPTS_CHART || dw.widgetType !== WIDGET_TYPE_TIME_ATTEMPTS_CHART) &&
          (FEATURE_USE_COMPLETED_ASSIGNMENT  || dw.widgetType !== WIDGET_TYPE_COMPLETED_ASSIGNMENT) &&
          (FEATURE_USE_LOGIN_INSIGHTS || dw.widgetType !== WIDGET_TYPE_LOGIN_PIE_CHART));
    })
    .map(dw => ({ ...dw, widget: this.widgetComponent(dw) })))

  // Todo: include a validation in the backend or a flag in the database to identify this
  isASRLEnglishVersion = (srlSelectedOption) => srlSelectedOption.label.toLowerCase().includes('english');

  widgetComponent = (dw) => {
    const currentSurveyList = get(this.props.surveys, 'srlSurveyList', []);
    let currentSurvey = {};
    let isEnglishVersion = false;
    if (currentSurveyList.length > 0) {
      currentSurvey = currentSurveyList.find(survey => survey.id === this.state.srlSelectedOption.value) || {};
      isEnglishVersion = this.isASRLEnglishVersion(this.state.srlSelectedOption);
    }
    return (
      <WidgetWrapper
        limit={3}
        widget={{ widget: dw }}
        courseId={this.props.courseAbstract.id}
        courseShortId={this.props.courseAbstract.shortId}
        productModelId={this.props.courseAbstract.productModelId}
        activityId={currentSurvey.activity_id}
        tool={currentSurvey.tool}
        isDetailView={false}
        hasStudents={this.props.courseAbstract.enrollments > 0}
        onTabChange={this.props.onTabChange}
        toggleInsightDetailsModal={this.props.toggleInsightDetailsModal}
        sendAnalyticsEvent={this.props.sendAnalyticsEvent}
        isSRLIntroSurvey={currentSurvey.is_intro_survey}
        isSRLEnglishVersion={isEnglishVersion}
        isSRLFirstCheckpoint={currentSurvey.is_first_checkpoint}
      />
    );
  }

  handleSRLDropdownOpen = (event) => {
    event.stopPropagation();
    this.setState({ srlDropdownAnchor: event.currentTarget });
  }

  handleSRLDropdownClick = (item, event) => {
    event.stopPropagation();
    const surveyResults = get(this.props.surveys, 'srlSurveyResults', {});
    // Only fetch survey data if we haven't already gotten it
    if (!surveyResults[item.value]) {
      this.props.getSRLSurveyResults(this.props.courseAbstract.id, item.value);
    }
    this.setState({ srlSelectedOption: item, srlDropdownAnchor: null });
    this.props.sendAnalyticsEvent(selectAssignment(item));
  }

  handleSRLDropdownClose = () => {
    this.setState({ srlDropdownAnchor: null });
  };

  renderInsightsDashboard = () => {
    const {
      onTabChange,
      widgetsV2,
      courseAbstract,
      widgetDetailModalVisible,
    } = this.props;
    const widgets = widgetsV2;

    const hasWidgets = Boolean(widgets.length);
    const courseId = courseAbstract.id;
    const { enrollments } = courseAbstract;
    const hasStudents = enrollments > 0;
    const widgetsComponents = this.getWidgetComponents(widgets);
    const widgetDataForDetailModal = widgets.find(w => w.dashboardWidgetId === widgetDetailModalVisible);

    return (
      <Fragment>
         {hasWidgets ? (
           <GridLayout
              widgets={widgetsComponents}
              onWidthChange={this.props.containerWidthChange}
              hidePadding
              margin={[24, 24]}
           />
         ) : (
          <WidgetEmptyState />
        )}
        <ReactTooltip className={style.toolTipText} effect="solid" multiline />

        {widgetDetailModalVisible !== 0 && widgetDataForDetailModal && (
          <WidgetWrapper
            isDetailView
            isInsightDetailModalVisible
            key={`detail_${courseId}_${widgetDataForDetailModal.dashboardWidgetId}`}
            widget={{ widget: widgetDataForDetailModal }}
            courseId={courseId}
            hasStudents={hasStudents}
            onTabChange={onTabChange}
            toggleInsightDetailsModal={this.props.toggleInsightDetailsModal}
            sendAnalyticsEvent={this.props.sendAnalyticsEvent}
          />
        )}
      </Fragment>
    );
  }

  renderSRLDropdown = () => {
    const srlSurveyList = get(this.props.surveys, 'srlSurveyList', []);
    const srlDropdownOptions = srlSurveyList.map(survey => ({ label: survey.name, value: survey.id }));

    if (srlDropdownOptions.length > 0) {
      return (
        <div className={style.srlDropdownContainer}>
          <span> Showing Results for: </span>
          <CustomMuiDropdown
            className={style.srlDropdown}
            labelClassName={style.srlDropdownLabel}
            optionsClassName={style.srlDropdownOptions}
            items={srlDropdownOptions}
            selectedValue={this.state.srlSelectedOption.value}
            onClick={this.handleSRLDropdownOpen}
            onClickItem={this.handleSRLDropdownClick}
            onClose={this.handleSRLDropdownClose}
          />
        </div>
      );
    }
    return null;
  };

  renderSRLDashboard = () => {
    if (!this.state.srlSelectedOption.value) {
      return (
        <EmptySRLDashboard />
      );
    } else if (this.state.srlSelectedOption.value) {
      const selectedResults = get(this.props.surveys, `srlSurveyResults[${this.state.srlSelectedOption.value}]`, []);
      const srlWidgetsComponents = this.getWidgetComponents(selectedResults);
        return (
          <GridLayout
            widgets={srlWidgetsComponents}
            hidePadding
            onWidthChange={this.props.containerWidthChange}
            enableResponsiveHeight
            widgetHeight={this.props.SRLWidgetsHeight}
            margin={[24, 24]}
          />
        );
    }
    return null;
  }

  renderDashboard = () => {
    if (this.props.widgetsV2) {
      return this.renderInsightsDashboard();
    }

    this.props.getSRLDropdown(this.renderSRLDropdown());
    this.props.setSelectedSurveyAssignmentId(this.state.srlSelectedOption.value);
    return this.renderSRLDashboard();
  }

  render() {
    const { loading } = this.props;

    if (loading) return <Loader />;

    return (
      <div>
        {this.renderDashboard()}
      </div>
    );
  }
}

function mapStateToProps(state) {
  const loading = get(state, 'insights.isDashboardLoading');
  const widgetDetailModalVisible = get(state, 'insights.widgetDetailModalVisible', false);
  const SRLWidgetsHeight =  get(state, 'insights.SRLWidgetsHeight');
  const assignments = get(state, 'insights.dashboard.assignments', []);

  return {
    loading,
    widgetDetailModalVisible,
    surveys: state.insights.surveys,
    SRLWidgetsHeight,
    assignments,
  };
}

const mapDispatchToProps = dispatch => bindActionCreators({
  toggleInsightDetailsModal,
  sendAnalyticsEvent,
  getDashboardsWidgets,
  getSRLSurveys,
  getSRLSurveyResults,
  containerWidthChange,
  updateSRLWidgetsHeight,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(QuantQualInsights);
